import { useState, useEffect, useRef } from 'react';
import { X, Minus, Plus, Trash2, MapPin, Mail, Navigation, Loader2, CheckCircle, MessageCircle, Tag, ChevronDown, ExternalLink } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { trackPurchase, trackBeginCheckout } from '@/lib/analytics';
import type { Product } from '@/data/allProducts';
import { getOptimizedImageUrl } from '@/data/allProducts';
import InstapayCheckout, { getShippingFee, OPEN_BEFORE_PICKUP_FEE } from '@/components/storefront/InstapayCheckout';

export interface CartItem extends Product {
  quantity: number;
}

interface CartDrawerProps {
  open: boolean;
  onClose: () => void;
  items: CartItem[];
  onUpdateQty: (id: string, qty: number) => void;
  onRemove: (id: string) => void;
  onClearCart?: () => void;
}

// ── Shipping zones (must match InstapayCheckout.tsx) ────────────────
const SHIPPING_ZONES: { label: string; cities: string[]; fee: number }[] = [
  { label: 'Cairo, Giza, Alexandria & Behira',  cities: ['Cairo','Giza','Alexandria','Behira','Damanhur'], fee: 100 },
  { label: 'Delta & Canal',                     cities: ['Dakahlia','El Kalioubia','Gharbia','Kafr Alsheikh','Monufia','Sharqia','Damietta','Ismailia','Port Said','Suez','Mansoura','Tanta','Zagazig'], fee: 110 },
  { label: 'Near Upper Egypt',                  cities: ['Fayoum','Beni Suef','Minya','Asyut','Sohag'], fee: 125 },
  { label: 'Far Upper Egypt & Matrouh',         cities: ['Qena','Luxor','Aswan','Red Sea','Matrouh','Hurghada'], fee: 145 },
  { label: 'North Coast',                       cities: ['North Coast'], fee: 150 },
  { label: 'Sinai & New Valley',                cities: ['North Sinai','South Sinai','New Valley'], fee: 165 },
];

const HIGH_VALUE_THRESHOLD = 2500;
const COD_FEE = 50;

// ── Shipping discount rules ─────────────────────────────────────────
function getShippingDiscount(subtotal: number, itemCount: number): number {
  if (itemCount >= 4 && subtotal >= 1500) return 0.5;
  if (itemCount >= 3 && subtotal >= 2000) return 0.5;
  if (itemCount >= 2 && subtotal >= 3000) return 0.5;
  return 0;
}

// ── Auto-promo rules: applied automatically when criteria met ────────
const AUTO_PROMOS: Array<{
  code: string;
  type: 'percent' | 'fixed' | 'shipping';
  value: number;
  label: string;
  check: (subtotal: number, qty: number) => boolean;
}> = [
  {
    code: 'AUTO-SHIP50-4ITEMS',
    type: 'shipping', value: 0,
    label: '50% shipping discount — 4+ items over EGP 1,500',
    check: (s, q) => q >= 4 && s >= 1500,
  },
  {
    code: 'AUTO-SHIP50-3ITEMS',
    type: 'shipping', value: 0,
    label: '50% shipping discount — 3+ items over EGP 2,000',
    check: (s, q) => q >= 3 && s >= 2000,
  },
  {
    code: 'AUTO-SHIP50-2ITEMS',
    type: 'shipping', value: 0,
    label: '50% shipping discount — 2+ items over EGP 3,000',
    check: (s, q) => q >= 2 && s >= 3000,
  },
];

// ── Promo codes (DB-backed validation done at checkout) ─────────────
// These are checked against Supabase promo_codes table in handleCheckout
const PROMO_CODES_LOCAL: Record<string, { type: 'percent' | 'fixed' | 'shipping'; value: number; label: string }> = {
  'WELCOME10': { type: 'percent',  value: 10, label: '10% off your first order' },
  'WELCOME15': { type: 'percent',  value: 15, label: '15% off your first order' },
  'FREESHIP':  { type: 'shipping', value: 0,  label: 'Free shipping applied' },
  'FAMILY10':  { type: 'percent',  value: 10, label: '10% loyalty discount' },
  'LOYALTY15': { type: 'percent',  value: 15, label: '15% loyalty discount' },
};

const inputCls = (err?: boolean) =>
  `w-full px-3 py-2 rounded-lg bg-secondary/50 border text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary/50 transition-colors ${
    err ? 'border-destructive focus:ring-destructive' : 'border-border'
  }`;
const labelCls = 'block text-xs font-medium text-muted-foreground mb-1';
const errCls   = 'text-[10px] text-destructive mt-0.5';

const PAYMENT_METHODS = [
  { value: 'instapay',   label: 'InstaPay',           note: 'We collect',                  icon: '💜' },
  { value: 'cod',        label: 'Cash on Delivery',   note: 'Cash on Delivery fee 50 EGP', icon: '💵' },
  { value: 'wallet',     label: 'Mobile Wallet',      note: 'We collect',                  icon: '📱' },
];

const CartDrawer = ({ open, onClose, items, onUpdateQty, onRemove, onClearCart }: CartDrawerProps) => {
  const { t, i18n } = useTranslation();
  const isRtl = i18n.language === 'ar';

  // Shipping
  const [deliveryCity,     setDeliveryCity]     = useState('');
  const [openBeforePickup, setOpenBeforePickup] = useState(false);
  const allowOpenBeforePickup = items.some(item => (item as any).allow_open_before_pickup === true);

  // Payment
  const [paymentMethod,  setPaymentMethod]  = useState('cod');
  const [showInstapay,   setShowInstapay]   = useState(false);
  const [showWallet,     setShowWallet]     = useState(false);

  const [placedOrderRef, setPlacedOrderRef] = useState('');

  // Promo
  const [promoInput,   setPromoInput]   = useState('');
  const [appliedPromo, setAppliedPromo] = useState<null | { code: string; type: 'percent' | 'fixed' | 'shipping'; value: number; label: string }>(null);
  const [promoError,   setPromoError]   = useState('');
  const [isCheckingPromo, setIsCheckingPromo] = useState(false);

  // Contact
  const [fullName,  setFullName]  = useState('');
  const [email,     setEmail]     = useState('');
  const [mobile,    setMobile]    = useState('');
  const [street,    setStreet]    = useState('');
  const [building,  setBuilding]  = useState('');
  const [apartment, setApartment] = useState('');
  const [landmark,  setLandmark]  = useState('');
  const [mapsLink,  setMapsLink]  = useState('');

  const [errors,       setErrors]       = useState<Record<string, string>>({});
  const [submitted,    setSubmitted]    = useState(false);
  const [isPlacing,    setIsPlacing]    = useState(false);
  const [orderSuccess, setOrderSuccess] = useState<string | null>(null);

  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Holds the exact payload we just inserted for the current order, so the
  // InstaPay/Wallet "confirm payment" steps can re-use it (merged with the
  // updated status) instead of re-selecting the row from `orders` — that
  // keeps the confirm flow working without needing a public SELECT policy
  // on a table that holds customer PII.
  const placedOrderDataRef = useRef<Record<string, unknown> | null>(null);

  // Cleanup timer on unmount to prevent state updates on unmounted component
  useEffect(() => () => { if (closeTimer.current) clearTimeout(closeTimer.current); }, []);

  const handleClose = () => {
    setShowInstapay(false);
    setShowWallet(false);
    setOrderSuccess(null);
    setPlacedOrderRef('');
    // Release scroll lock before closing
    document.body.style.overflow = '';
    document.body.style.position = '';
    document.body.style.top = '';
    document.body.style.width = '';
    onClose();
  };

  // ── Computed values ──────────────────────────────────────────────
  const subtotal      = items.reduce((s, i) => s + i.price * i.quantity, 0);
  const totalQty      = items.reduce((s, i) => s + i.quantity, 0);
  const isHighValue   = subtotal >= HIGH_VALUE_THRESHOLD;
  const rawShipping   = deliveryCity ? getShippingFee(deliveryCity) : 0;
  const shipDiscount  = getShippingDiscount(subtotal, totalQty);
  // Shipping is free if: shipping promo applied OR auto-ship discount (50% off shown separately)
  const isShippingFree = appliedPromo?.type === 'shipping' && appliedPromo.code.startsWith('AUTO-');
  const baseShipping  = appliedPromo?.type === 'shipping' && !appliedPromo.code.startsWith('AUTO-') ? 0 : rawShipping;
  const shippingFee   = isShippingFree ? Math.round(rawShipping * 0.5) : baseShipping;
  const openFee       = (openBeforePickup && allowOpenBeforePickup) ? OPEN_BEFORE_PICKUP_FEE : 0;
  const codFee        = paymentMethod === 'cod' ? COD_FEE : 0;
  const promoDiscount = appliedPromo
    ? appliedPromo.type === 'percent' ? Math.round(subtotal * appliedPromo.value / 100)
    : appliedPromo.type === 'fixed'   ? appliedPromo.value : 0 : 0;
  const total = Math.max(0, subtotal - promoDiscount + shippingFee + openFee + codFee);

  // Auto-lock COD for high value
  useEffect(() => {
    if (isHighValue && paymentMethod === 'cod') setPaymentMethod('instapay');
  }, [isHighValue, subtotal]);

  // Auto-apply shipping discount when criteria met — only if no manual promo applied
  useEffect(() => {
    // Don't override a manually applied promo
    if (appliedPromo && !appliedPromo.code.startsWith('AUTO-')) return;

    const matchingAuto = AUTO_PROMOS.find(p => p.check(subtotal, totalQty));

    if (matchingAuto) {
      // Apply or update auto promo
      if (!appliedPromo || appliedPromo.code !== matchingAuto.code) {
        setAppliedPromo({ code: matchingAuto.code, type: matchingAuto.type, value: matchingAuto.value, label: matchingAuto.label });
      }
    } else {
      // Remove auto promo if conditions no longer met
      if (appliedPromo?.code.startsWith('AUTO-')) {
        setAppliedPromo(null);
      }
    }
  }, [subtotal, totalQty]);

  // Reset on close
  useEffect(() => {
    if (!open) {
      setSubmitted(false); setErrors({}); setShowInstapay(false); setShowWallet(false);
      setOrderSuccess(null); setPlacedOrderRef('');
    }
  }, [open]);

  // Scroll lock
  useEffect(() => {
    if (open) {
      const scrollY = window.scrollY;
      document.body.style.overflow = 'hidden';
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.width = '100%';
      return () => {
        const top = document.body.style.top;
        document.body.style.overflow = '';
        document.body.style.position = '';
        document.body.style.top = '';
        document.body.style.width = '';
        window.scrollTo(0, parseInt(top || '0') * -1);
      };
    } else {
      const scrollY = document.body.style.top;
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
      window.scrollTo(0, parseInt(scrollY || '0') * -1);
    }
    return () => {
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
    };
  }, [open]);

  // ── Promo validation (DB-backed: checks usage by email/phone) ────
  const applyPromo = async () => {
    const code = promoInput.trim().toUpperCase();
    if (!code) return;
    if (appliedPromo) { setPromoError('Only one promo code per order.'); return; }

    setIsCheckingPromo(true);
    setPromoError('');
    try {
      // Check against DB promo_codes table
      const { data: promoRow } = await supabase
        .from('promo_codes')
        .select('*')
        .eq('code', code)
        .eq('is_active', true)
        .single();

      if (!promoRow) {
        // Fall back to local codes
        const local = PROMO_CODES_LOCAL[code];
        if (local) {
          setAppliedPromo({ code, ...local });
          setPromoInput('');
        } else {
          setPromoError('Invalid or expired promo code.');
        }
        return;
      }

      // Check if same customer already used this code
      if (email.trim() || mobile.trim()) {
        const { data: usageRows } = await supabase
          .from('promo_usage')
          .select('id')
          .eq('code', code)
          .or(`customer_email.eq.${email.trim()},customer_phone.eq.${mobile.trim()}`)
          .limit(1);

        if (usageRows && usageRows.length > 0) {
          setPromoError('You have already used this promo code.');
          return;
        }
      }

      // Check global usage cap — max_uses null means unlimited
      if (promoRow.max_uses !== null && promoRow.uses_count >= promoRow.max_uses) {
        setPromoError('This promo code has reached its usage limit.');
        return;
      }

      // Check expiry
      if (promoRow.expires_at && new Date(promoRow.expires_at) < new Date()) {
        setPromoError('This promo code has expired.');
        return;
      }

      setAppliedPromo({ code, type: promoRow.type, value: promoRow.value, label: promoRow.label });
      setPromoInput('');
    } catch {
      // If tables don't exist yet, fall back to local
      const local = PROMO_CODES_LOCAL[code];
      if (local) { setAppliedPromo({ code, ...local }); setPromoInput(''); }
      else setPromoError('Invalid promo code.');
    } finally {
      setIsCheckingPromo(false);
    }
  };

  const validate = () => {
    const e: Record<string, string> = {};
    if (!fullName.trim() || fullName.trim().length < 2)                        e.fullName = 'Full name is required';
    if (!email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) e.email = 'Valid email is required';
    if (!mobile.trim() || mobile.replace(/\D/g, '').length < 10)      e.mobile = 'Valid mobile number is required';
    if (!deliveryCity)                                                  e.city = 'Please select your city';
    if (!street.trim())                                                 e.street = 'Street name is required';
    if (!building.trim())                                               e.building = 'Building number is required';
    if (!apartment.trim())                                              e.apartment = 'Apartment number is required';
    return e;
  };

  const handleCheckout = async () => {
    setSubmitted(true);
    const e = validate();
    setErrors(e);
    if (Object.keys(e).length > 0) return;

    setIsPlacing(true);
    try {
      // Timestamp + random suffix — the timestamp keeps it sortable/human
      // readable, the random suffix stops it being guessable purely from
      // "when did they order" (it doubles as the lookup key for order
      // tracking, so it shouldn't be brute-forceable).
      const orderRef = `ZKO-${Date.now().toString(36).toUpperCase()}${Math.random().toString(36).slice(2, 6).toUpperCase()}`;
      trackBeginCheckout(items, total);

      const orderPayload = {
        order_number:       orderRef,
        customer_email:     email.trim(),
        customer_phone:     mobile.trim(),
        customer_name:      fullName.trim(),
        customer_address:   `${street}, Bldg ${building}, Apt ${apartment}${landmark ? ` (${landmark})` : ''}`,
        customer_city:      deliveryCity,
        payment_method:     paymentMethod,
        shipping_fee:       shippingFee,
        open_before_pickup: openBeforePickup && allowOpenBeforePickup,
        delivery_city:      deliveryCity,
        promo_code:         appliedPromo?.code || null,
        subtotal,
        cod_fee:            codFee,
        discount:           promoDiscount,
        total,
        source:             'storefront',
        status:             paymentMethod === 'instapay' ? 'pending_verification' : 'pending',
        items_count:        totalQty,
        items_detail:       items.map(i => ({ id: i.id, title: i.title, price: i.price, quantity: i.quantity })),
      };

      const { error: dbError } = await supabase.from('orders').insert([orderPayload]);
      if (dbError) throw dbError;
      placedOrderDataRef.current = orderPayload;

      // Record promo usage to prevent reuse
      if (appliedPromo) {
        await supabase.from('promo_usage').insert([{
          code:            appliedPromo.code,
          order_number:    orderRef,
          customer_email:  email.trim(),
          customer_phone:  mobile.trim(),
        }]).catch(() => {});
      }

      trackPurchase({ orderRef, total, subtotal, discount: promoDiscount, paymentMethod, items });
      // COD: notify immediately. InstaPay & Wallet: notify after payment confirmed (handled in their overlays)
      if (paymentMethod === 'cod') {
        supabase.functions.invoke('order-notify', { body: { order: { ...orderPayload, items } } }).catch(() => {});
      }

      setPlacedOrderRef(orderRef);

      if (paymentMethod === 'wallet') {
        setShowWallet(true);
        return;
      }
      if (paymentMethod === 'instapay') {
        setShowInstapay(true);
        return;
      }

      setOrderSuccess(orderRef);
      onClearCart?.();
      if (closeTimer.current) clearTimeout(closeTimer.current);
      closeTimer.current = setTimeout(() => { handleClose(); }, 3500);
      toast.success(`Thanks ${fullName.split(' ')[0]}! 🎉 Your order is on its way.`);
    } catch (err: any) {
      toast.error('Failed to place order: ' + (err.message || 'Please try again'));
    } finally {
      setIsPlacing(false);
    }
  };

  const handleMapsLinkFocus = async () => {
    if (mapsLink) return;
    try {
      const text = await navigator.clipboard.readText();
      if (text.includes('maps.google') || text.includes('goo.gl/maps') || text.includes('maps.app.goo')) setMapsLink(text.trim());
    } catch {}
  };


  // ── Mobile Wallet overlay ─────────────────────────────────────
  if (showWallet && placedOrderRef) {
    return (
      <AnimatePresence>
        {open && (
          <>
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              className="fixed inset-0 z-[2000000] bg-background/60 backdrop-blur-sm" onClick={handleClose} />
            <motion.div
              initial={{ x: isRtl ? '-100%' : '100%' }} animate={{ x: 0 }} exit={{ x: isRtl ? '-100%' : '100%' }}
              transition={{ type: 'spring', damping: 25, stiffness: 200 }}
              className={`fixed top-0 bottom-0 z-[2000001] w-full max-w-md glass-strong border-border flex flex-col overflow-y-auto ${isRtl ? 'left-0 border-e' : 'right-0 border-s'}`}
            >
              <div className="space-y-5 p-5">
                {/* Header */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2.5">
                    <div className="h-9 w-9 rounded-full bg-red-500/20 flex items-center justify-center text-lg">📱</div>
                    <div>
                      <h3 className="font-bold text-base">Pay via Mobile Wallet</h3>
                      <p className="text-xs text-muted-foreground">Order #{placedOrderRef}</p>
                    </div>
                  </div>
                  <button onClick={handleClose} aria-label={t('common.close')} className="p-1.5 text-muted-foreground hover:text-foreground rounded-md hover:bg-secondary transition-colors">
                    <X className="h-4 w-4" />
                  </button>
                </div>

                {/* Amount */}
                <div className="p-5 rounded-2xl bg-red-500/10 border border-red-500/20 text-center space-y-1">
                  <p className="text-xs text-muted-foreground">Transfer exactly</p>
                  <p className="text-4xl font-bold text-red-400">{total.toLocaleString()} EGP</p>
                  <p className="text-xs text-muted-foreground mt-1">Order EGP {subtotal.toLocaleString()} + Shipping EGP {shippingFee}</p>
                  <p className="text-xs text-amber-400 font-semibold pt-1">Write <span className="font-mono font-bold">{placedOrderRef}</span> as the transfer note</p>
                </div>

                {/* QR + Account details */}
                <div className="grid grid-cols-2 gap-3">
                  <div className="flex flex-col items-center gap-2 p-3 rounded-xl border border-border bg-white">
                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Scan QR</p>
                    <img src="/voda-mob.jpeg" alt="Vodafone Cash QR" className="w-28 h-28 object-contain rounded-lg" />
                    <a href="http://vf.eg/vfcash?id=mt&qrId=75Weao" target="_blank" rel="noopener noreferrer"
                      className="flex items-center gap-1 text-xs font-semibold text-red-600 hover:text-red-700 transition-colors">
                      <ExternalLink className="h-3 w-3" /> Pay directly
                    </a>
                  </div>
                  <div className="flex flex-col justify-center gap-3 p-3 rounded-xl border border-border bg-secondary/30">
                    <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Account Details</p>
                    <div className="space-y-2 text-sm">
                      <div>
                        <p className="text-[10px] text-muted-foreground uppercase">{t('cart.mobile')}</p>
                        <p className="font-bold font-mono">01037530503</p>
                      </div>
                      <div>
                        <p className="text-[10px] text-muted-foreground uppercase">Name</p>
                        <p className="font-semibold">Islam Sayed Zaky</p>
                      </div>
                      <div>
                        <p className="text-[10px] text-muted-foreground uppercase">Network</p>
                        <p className="font-semibold text-red-400">Vodafone</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Steps */}
                <div className="p-4 rounded-xl bg-amber-500/10 border border-amber-500/20 space-y-2">
                  <p className="text-sm font-semibold text-amber-400 flex items-center gap-2">⚡ How to Complete Payment</p>
                  <ol className="text-xs text-muted-foreground space-y-1.5 list-none">
                    {[
                      'Open any mobile wallet app or scan the QR — or click the link to pay directly',
                      `Transfer exactly EGP ${total.toLocaleString()} to 01037530503`,
                      `Enter ${placedOrderRef} as the transfer note/reference`,
                      'Take a screenshot of the successful transfer',
                      'Send screenshot to our WhatsApp below',
                    ].map((step, i) => (
                      <li key={i} className="flex gap-2">
                        <span className="shrink-0 h-4 w-4 rounded-full bg-amber-500/20 text-amber-400 text-[10px] font-bold flex items-center justify-center">{i+1}</span>
                        <span>{step}</span>
                      </li>
                    ))}
                  </ol>
                </div>

                {/* Status note */}
                <div className="p-3 rounded-xl bg-primary/10 border border-primary/20 flex gap-2 text-xs text-muted-foreground">
                  <span className="text-primary shrink-0 mt-0.5">ℹ️</span>
                  <span>Your order stays in <span className="text-primary font-semibold">Processing</span> status until our team verifies the transfer. We'll confirm and proceed with shipment via WhatsApp within <span className="font-semibold text-foreground">1 hour</span>.</span>
                </div>

                {/* WhatsApp screenshot */}
                <div className="p-4 rounded-xl border border-[#25D366]/30 bg-[#25D366]/5 space-y-3">
                  <p className="text-sm font-semibold text-[#25D366] flex items-center gap-2">
                    <MessageCircle className="h-4 w-4" /> Send Your Screenshot Here
                  </p>
                  <div className="flex items-center gap-4">
                    <div className="flex flex-col items-center gap-1 shrink-0">
                      <img src="/zm-whatsapp.jpeg" alt="WhatsApp QR" className="w-16 h-16 rounded-lg border border-[#25D366]/30 bg-white object-contain" />
                      <p className="text-[10px] text-muted-foreground">Scan to open</p>
                    </div>
                    <div className="flex-1 space-y-2">
                      <p className="text-xs text-muted-foreground">WhatsApp<br/><span className="font-bold text-foreground">+20 1037530503</span></p>
                      <a href="https://wa.me/message/ACL5MCRFCEGOD1" target="_blank" rel="noopener noreferrer"
                        className="flex items-center justify-center gap-2 w-full px-4 py-2.5 rounded-xl bg-[#25D366] text-black text-sm font-bold hover:bg-[#1ebe5a] transition-colors">
                        <MessageCircle className="h-4 w-4" /> Open WhatsApp
                      </a>
                      <p className="text-[10px] text-muted-foreground text-center">Send screenshot + order number<br/><span className="font-mono font-bold text-foreground">{placedOrderRef}</span></p>
                    </div>
                  </div>
                </div>

                {/* Confirm button */}
                <button onClick={async () => {
                    await supabase.from('orders').update({ status: 'pending_verification' }).eq('order_number', placedOrderRef);
                    if (placedOrderDataRef.current) {
                      const ord = { ...placedOrderDataRef.current, status: 'pending_verification' };
                      supabase.functions.invoke('order-notify', { body: { order: ord } }).catch(() => {});
                    }
                    setShowWallet(false); onClearCart?.(); handleClose(); toast.success(`شكراً ${fullName.split(' ')[0]}! 😊 Thanks ${fullName.split(' ')[0]}! Your order is confirmed. We'll contact you via email & WhatsApp.`, { duration: 6000 });
                  }}
                  className="w-full flex items-center justify-center gap-2 py-4 rounded-xl bg-primary text-primary-foreground text-sm font-bold hover:bg-primary/90 transition-colors">
                  <CheckCircle className="h-4 w-4" /> I've Sent the Screenshot — Confirm My Order
                </button>
                <p className="text-xs text-center text-muted-foreground">We will verify your transfer and confirm shipment within 1 hour.</p>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    );
  }

  // ── InstaPay overlay — z-[200] beats chatbot z-[150] ────────────
  if (showInstapay && placedOrderRef) {
    return (
      <AnimatePresence>
        {open && (
          <>
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              className="fixed inset-0 z-[2000000] bg-background/60 backdrop-blur-sm" onClick={handleClose} />
            <motion.div
              initial={{ x: isRtl ? '-100%' : '100%' }} animate={{ x: 0 }} exit={{ x: isRtl ? '-100%' : '100%' }}
              transition={{ type: 'spring', damping: 25, stiffness: 200 }}
              className={`fixed top-0 bottom-0 z-[2000001] w-full max-w-md glass-strong border-border flex flex-col overflow-y-auto ${isRtl ? 'left-0 border-e' : 'right-0 border-s'}`}
            >
              <InstapayCheckout
                orderNumber={placedOrderRef}
                orderData={placedOrderDataRef.current}
                orderTotal={subtotal - promoDiscount}
                shippingFee={shippingFee + openFee}
                onClose={handleClose}
                onConfirmed={() => { onClearCart?.(); handleClose(); toast.success(`شكراً ${fullName.split(' ')[0]}! 😊 Thanks ${fullName.split(' ')[0]}! Your order is confirmed. We'll contact you via email & WhatsApp.`, { duration: 6000 }); }}
              />
            </motion.div>
          </>
        )}
      </AnimatePresence>
    );
  }

  return (
    <AnimatePresence>
      {open && (
        <>
          {/* Backdrop — z-[200] hides chatbot */}
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-[2000000] bg-background/60 backdrop-blur-sm" onClick={handleClose} />

          {/* Drawer — z-[201] sits above backdrop */}
          <motion.div
            initial={{ x: isRtl ? '-100%' : '100%' }} animate={{ x: 0 }} exit={{ x: isRtl ? '-100%' : '100%' }}
            transition={{ type: 'spring', damping: 25, stiffness: 200 }}
            className={`fixed top-0 bottom-0 z-[2000001] w-full max-w-md glass-strong border-border flex flex-col ${isRtl ? 'left-0 border-e' : 'right-0 border-s'}`}
          >
            {/* Header */}
            <div className="flex items-center justify-between p-5 border-b border-border shrink-0">
              <h2 className="font-display text-lg font-bold">{t('cart.yourCart')} ({items.length})</h2>
              <button onClick={handleClose} aria-label={t('common.close')} className="p-1 text-muted-foreground hover:text-foreground"><X className="h-5 w-5" /></button>
            </div>

            <div className="flex-1 overflow-y-auto scrollbar-hide">

              {/* Cart items */}
              <div className="p-5 space-y-3 border-b border-border/50">
                {items.length === 0
                  ? <p className="text-center text-muted-foreground py-10">{t('cart.emptyCart')}</p>
                  : items.map(item => (
                    <div key={item.id} className="flex gap-3 p-3 rounded-lg bg-secondary/30">
                      <div className="w-14 h-14 rounded-md bg-secondary shrink-0 overflow-hidden flex items-center justify-center text-muted-foreground/30 font-display font-bold">
                        {item.image ? <img src={getOptimizedImageUrl(item.image, 160)} onError={(e) => { if (e.currentTarget.src !== item.image) e.currentTarget.src = item.image; }} alt={item.title} className="w-full h-full object-cover" /> : item.category[0]}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">{item.title}</p>
                        <p className="text-sm text-primary font-semibold mt-0.5">{t('products.egp')} {item.price}</p>
                        <div className="flex items-center gap-2 mt-1.5">
                          <button onClick={() => onUpdateQty(item.id, item.quantity - 1)} aria-label={t('common.decreaseQuantity')} className="p-1 rounded bg-secondary text-muted-foreground hover:text-foreground"><Minus className="h-3 w-3" /></button>
                          <span className="text-sm w-6 text-center">{item.quantity}</span>
                          <button onClick={() => onUpdateQty(item.id, item.quantity + 1)} aria-label={t('common.increaseQuantity')} className="p-1 rounded bg-secondary text-muted-foreground hover:text-foreground"><Plus className="h-3 w-3" /></button>
                          <button onClick={() => onRemove(item.id)} aria-label={t('common.removeItem')} className="ms-auto p-1 text-destructive/60 hover:text-destructive"><Trash2 className="h-3.5 w-3.5" /></button>
                        </div>
                      </div>
                    </div>
                  ))
                }
              </div>

              {items.length > 0 && (
                <div className="p-5 space-y-5">

                  {/* ── Contact ── */}
                  <div className="space-y-3">
                    <p className="text-xs font-semibold text-foreground uppercase tracking-wider flex items-center gap-1.5">
                      <Mail className="h-3.5 w-3.5 text-primary" /> Contact Info
                    </p>
                    <div>
                      <label className={labelCls}>{t('cart.fullName')} <span className="text-destructive">*</span></label>
                      <input type="text" value={fullName} onChange={e => { setFullName(e.target.value); if (submitted) setErrors(v => ({ ...v, fullName: '' })); }}
                        placeholder="Your full name" className={inputCls(submitted && !!errors.fullName)} />
                      {submitted && errors.fullName && <p className={errCls}>{errors.fullName}</p>}
                    </div>
                    <div>
                      <label className={labelCls}>{t('cart.email')} <span className="text-destructive">*</span></label>
                      <input type="email" value={email} onChange={e => { setEmail(e.target.value); if (submitted) setErrors(v => ({ ...v, email: '' })); }}
                        placeholder="your@email.com" className={inputCls(submitted && !!errors.email)} />
                      {submitted && errors.email && <p className={errCls}>{errors.email}</p>}
                    </div>
                    <div>
                      <label className={labelCls}>Mobile Number <span className="text-destructive">*</span></label>
                      <div className="flex gap-2">
                        <span className="flex items-center px-2.5 rounded-lg bg-secondary/50 border border-border text-sm text-muted-foreground shrink-0">🇪🇬 +20</span>
                        <input type="tel" value={mobile} onChange={e => { setMobile(e.target.value); if (submitted) setErrors(v => ({ ...v, mobile: '' })); }}
                          placeholder="01X XXXX XXXX" className={inputCls(submitted && !!errors.mobile)} />
                      </div>
                      {submitted && errors.mobile && <p className={errCls}>{errors.mobile}</p>}
                    </div>
                  </div>

                  {/* ── Delivery ── */}
                  <div className="space-y-3 pt-4 border-t border-border/50">
                    <p className="text-xs font-semibold text-foreground uppercase tracking-wider flex items-center gap-1.5">
                      <MapPin className="h-3.5 w-3.5 text-primary" /> Delivery Address
                    </p>

                    {/* City selector — no prices in dropdown */}
                    <div>
                      <label className={labelCls}>City / Governorate <span className="text-destructive">*</span></label>
                      <div className="relative">
                        <select value={deliveryCity}
                          onChange={e => { setDeliveryCity(e.target.value); if (submitted) setErrors(v => ({ ...v, city: '' })); }}
                          className={`${inputCls(submitted && !!errors.city)} appearance-none pr-8`}>
                          <option value="">Select your city...</option>
                          {SHIPPING_ZONES.map(zone => (
                            <optgroup key={zone.label} label={zone.label}>
                              {zone.cities.map(city => <option key={city} value={city}>{city}</option>)}
                            </optgroup>
                          ))}
                        </select>
                        <ChevronDown className="absolute right-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
                      </div>
                      {submitted && errors.city && <p className={errCls}>{errors.city}</p>}
                    </div>

                    {/* Dynamic notes shown only after city selection */}
                    <AnimatePresence>
                      {deliveryCity && (
                        <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }}
                          exit={{ opacity: 0, height: 0 }} transition={{ duration: 0.2 }} className="overflow-hidden space-y-2">

                          {/* Shipping fee note */}
                          <div className="flex items-center justify-between text-xs px-3 py-2 rounded-lg bg-primary/5 border border-primary/15">
                            <span className="text-muted-foreground">
                              Shipping to <span className="font-semibold text-foreground">{deliveryCity}</span>
                            </span>
                            <span className="font-bold text-primary">
                              {shippingFee} EGP
                              {isShippingFree && (
                                <span className="ml-1.5 text-emerald-400 text-[10px] line-through opacity-60">{rawShipping} EGP</span>
                              )}
                            </span>
                          </div>

                          {/* Auto-promo banner — shown when criteria met */}
                          {appliedPromo?.code.startsWith('AUTO-') && (
                            <div className="px-3 py-2.5 rounded-lg bg-emerald-500/10 border border-emerald-500/20 text-xs text-emerald-300 flex items-start gap-2">
                              <span className="shrink-0">🎁</span>
                              <span><span className="font-semibold">Shipping discount auto-applied!</span> {appliedPromo.label}</span>
                            </div>
                          )}

                          {/* High value warning — only after city selection */}
                          {isHighValue && (
                            <div className="flex items-start gap-2 px-3 py-2 rounded-lg bg-violet-500/10 border border-violet-500/20 text-xs text-violet-300">
                              <span className="shrink-0 mt-0.5">⚠️</span>
                              <span>Orders above EGP {HIGH_VALUE_THRESHOLD.toLocaleString()} require InstaPay, Visa, or card payment — Cash on Delivery is not available.</span>
                            </div>
                          )}

                          {/* Open before pickup */}
                          {allowOpenBeforePickup && (
                            <label className="flex items-start gap-3 cursor-pointer group px-3 py-2.5 rounded-lg border border-border hover:border-primary/40 transition-colors">
                              <div className="relative mt-0.5 shrink-0">
                                <input type="checkbox" checked={openBeforePickup} onChange={e => setOpenBeforePickup(e.target.checked)} className="sr-only" />
                                <div className={`w-4.5 h-4.5 w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${openBeforePickup ? 'bg-primary border-primary' : 'border-border group-hover:border-primary/50'}`}>
                                  {openBeforePickup && <svg className="w-3 h-3 text-primary-foreground" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>}
                                </div>
                              </div>
                              <div>
                                <p className="text-xs font-medium">Open package before pickup <span className="text-amber-400 font-semibold">+{OPEN_BEFORE_PICKUP_FEE} EGP</span></p>
                                <p className="text-[10px] text-muted-foreground mt-0.5">Allow Bosta to open at your door for inspection.</p>
                              </div>
                            </label>
                          )}
                        </motion.div>
                      )}
                    </AnimatePresence>

                    {/* Street address — shown after city */}
                    <AnimatePresence>
                      {deliveryCity && (
                        <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }}
                          exit={{ opacity: 0, height: 0 }} transition={{ duration: 0.22 }} className="space-y-3 overflow-hidden">
                          <div>
                            <label className={labelCls}>Street Name <span className="text-destructive">*</span></label>
                            <input type="text" value={street} onChange={e => { setStreet(e.target.value); if (submitted) setErrors(v => ({ ...v, street: '' })); }}
                              placeholder="e.g. Tahrir Street" className={inputCls(submitted && !!errors.street)} />
                            {submitted && errors.street && <p className={errCls}>{errors.street}</p>}
                          </div>
                          <div className="grid grid-cols-2 gap-2">
                            <div>
                              <label className={labelCls}>Building No. <span className="text-destructive">*</span></label>
                              <input type="text" value={building} onChange={e => { setBuilding(e.target.value); if (submitted) setErrors(v => ({ ...v, building: '' })); }}
                                placeholder="e.g. 14" className={inputCls(submitted && !!errors.building)} />
                              {submitted && errors.building && <p className={errCls}>{errors.building}</p>}
                            </div>
                            <div>
                              <label className={labelCls}>Apartment No. <span className="text-destructive">*</span></label>
                              <input type="text" value={apartment} onChange={e => { setApartment(e.target.value); if (submitted) setErrors(v => ({ ...v, apartment: '' })); }}
                                placeholder="e.g. 3A" className={inputCls(submitted && !!errors.apartment)} />
                              {submitted && errors.apartment && <p className={errCls}>{errors.apartment}</p>}
                            </div>
                          </div>
                          <div>
                            <label className={labelCls}>Landmark <span className="text-muted-foreground/50 font-normal">(optional)</span></label>
                            <input type="text" value={landmark} onChange={e => setLandmark(e.target.value)}
                              placeholder="e.g. Near City Stars Mall" className={inputCls()} />
                          </div>
                          <div>
                            <label className={`${labelCls} flex items-center gap-1`}>
                              <Navigation className="h-3 w-3 text-primary" />
                              Google Maps Link <span className="text-muted-foreground/50 font-normal">(optional)</span>
                            </label>
                            <input type="url" value={mapsLink} onChange={e => setMapsLink(e.target.value)}
                              onFocus={handleMapsLinkFocus} placeholder="Paste your Google Maps link here" className={inputCls()} />
                            <p className="text-[10px] text-muted-foreground mt-1">Open Maps → long press location → Share → Copy link → paste above</p>
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>

                  {/* ── Payment methods ── */}
                  <div className="space-y-3 pt-4 border-t border-border/50">
                    <p className="text-xs font-semibold text-foreground uppercase tracking-wider">{t('cart.paymentMethod', 'Payment Method')}</p>
                    <div className="grid grid-cols-2 gap-2">
                      {PAYMENT_METHODS
                        .filter(m => {
                          // Filter high-value COD restriction
                          if (isHighValue && m.value === 'cod') return false;
                          // Filter by product's allowed payment methods (if specified)
                          const productMethods = items.length > 0 && items[0].payment_methods;
                          if (productMethods && productMethods.length > 0) {
                            return productMethods.includes(m.value);
                          }
                          return true;
                        })
                        .map(m => (
                          <button key={m.value} onClick={() => setPaymentMethod(m.value)}
                            className={`py-2.5 px-3 rounded-lg text-xs font-medium border transition-all text-left space-y-0.5 ${paymentMethod === m.value ? 'bg-primary text-primary-foreground border-primary' : 'bg-secondary/50 text-muted-foreground border-border hover:bg-secondary'}`}>
                            <span className="block font-semibold">{m.icon} {m.label}</span>
                            <span className={`block text-[10px] ${paymentMethod === m.value ? 'text-primary-foreground/70' : 'text-muted-foreground/60'}`}>{m.note}</span>
                          </button>
                        ))}
                    </div>

                    {/* Dynamic method notes — only when relevant method selected */}
                    {paymentMethod === 'instapay' && (
                      <div className="p-3 rounded-lg bg-violet-500/10 border border-violet-500/20 text-xs text-violet-300 flex items-start gap-2">
                        <MessageCircle className="h-4 w-4 shrink-0 mt-0.5" />
                        <span>After placing your order you'll receive full payment instructions with QR code, account details, and a WhatsApp link to confirm your transfer.</span>
                      </div>
                    )}
                    {paymentMethod === 'wallet' && (
                      <div className="mt-3 p-3 rounded-xl bg-red-500/10 border border-red-500/20 text-xs text-red-300 flex items-start gap-2">
                        <MessageCircle className="h-4 w-4 shrink-0 mt-0.5" />
                        <span>After placing your order you'll receive Vodafone Cash payment instructions with QR code, account details, and a WhatsApp link to confirm your transfer.</span>
                      </div>
                    )}
                    {paymentMethod === 'cod' && (
                      <p className="text-[10px] text-amber-400 flex items-center gap-1.5">
                        💵 Cash on Delivery fee of <span className="font-bold">EGP {COD_FEE}</span> is added to your total.
                      </p>
                    )}
                  </div>

                  {/* ── Promo Code — last, after payment ── */}
                  <div className="space-y-2 pt-4 border-t border-border/50">
                    <p className="text-xs font-semibold text-foreground uppercase tracking-wider flex items-center gap-1.5">
                      <Tag className="h-3.5 w-3.5 text-primary" /> Promo Code
                    </p>
                    {appliedPromo ? (
                      <div className="flex items-center justify-between px-3 py-2 rounded-lg bg-primary/10 border border-primary/20">
                        <div>
                          <span className="text-xs font-bold text-primary">{appliedPromo.code}</span>
                          <span className="text-xs text-muted-foreground ms-2">— {appliedPromo.label}</span>
                        </div>
                        <button onClick={() => setAppliedPromo(null)} aria-label={t('common.removePromoCode')} className="text-muted-foreground hover:text-destructive ms-2"><X className="h-3.5 w-3.5" /></button>
                      </div>
                    ) : (
                      <div className="flex gap-2">
                        <input type="text" value={promoInput}
                          onChange={e => { setPromoInput(e.target.value.toUpperCase()); setPromoError(''); }}
                          onKeyDown={e => e.key === 'Enter' && promoInput.trim() && applyPromo()}
                          placeholder="Enter promo code"
                          className={inputCls(!!promoError)} />
                        <button
                          onClick={applyPromo}
                          disabled={!promoInput.trim() || isCheckingPromo}
                          className="px-3 py-2 rounded-lg bg-secondary hover:bg-secondary/80 border border-border text-xs font-semibold text-foreground transition-all whitespace-nowrap disabled:opacity-30 disabled:cursor-not-allowed flex items-center gap-1.5"
                        >
                          {isCheckingPromo ? <Loader2 className="h-3 w-3 animate-spin" /> : 'Apply'}
                        </button>
                      </div>
                    )}
                    {promoError && <p className="text-xs text-destructive">{promoError}</p>}
                    <p className="text-[10px] text-muted-foreground">One promo code per order. Each code can only be used once per customer.</p>
                  </div>

                  {/* ── Order summary ── */}
                  <div className="space-y-2 pt-4 border-t border-border/50">
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Subtotal</span>
                      <span className="font-medium">{t('products.egp')} {subtotal.toLocaleString()}</span>
                    </div>
                    {promoDiscount > 0 && (
                      <div className="flex justify-between text-sm">
                        <span className="text-primary">Promo discount</span>
                        <span className="text-primary font-medium">− {t('products.egp')} {promoDiscount.toLocaleString()}</span>
                      </div>
                    )}
                    {deliveryCity ? (
                      appliedPromo?.type === 'shipping' && !appliedPromo.code.startsWith('AUTO-')
                        ? <div className="flex justify-between text-sm"><span className="text-primary">Shipping</span><span className="text-primary font-medium">Free 🎉</span></div>
                        : <div className="flex justify-between text-sm">
                            <span className="text-muted-foreground flex items-center gap-1">
                              Shipping
                              {isShippingFree && <span className="text-emerald-400 text-[10px] font-semibold">(50% off)</span>}
                            </span>
                            <span className="flex items-center gap-1.5">
                              {isShippingFree && <span className="text-muted-foreground/50 text-xs line-through">{rawShipping}</span>}
                              {t('products.egp')} {shippingFee}
                            </span>
                          </div>
                    ) : (
                      <p className="text-xs text-muted-foreground italic">Select your city to see shipping fee</p>
                    )}
                    {openBeforePickup && allowOpenBeforePickup && (
                      <div className="flex justify-between text-sm">
                        <span className="text-amber-400">Open before pickup</span>
                        <span className="text-amber-400">+ {t('products.egp')} {OPEN_BEFORE_PICKUP_FEE}</span>
                      </div>
                    )}
                    {paymentMethod === 'cod' && (
                      <div className="flex justify-between text-sm">
                        <span className="text-amber-400">Cash on Delivery fee</span>
                        <span className="text-amber-400">+ {t('products.egp')} {COD_FEE}</span>
                      </div>
                    )}
                    <div className="flex justify-between text-base font-bold border-t border-border pt-2">
                      <span>Total</span>
                      <span className="text-primary">{t('products.egp')} {total.toLocaleString()}</span>
                    </div>
                  </div>

                  {/* ── Place order / success ── */}
                  {orderSuccess ? (
                    <div className="text-center py-8 space-y-4 px-4">
                      <CheckCircle className="h-14 w-14 text-emerald-400 mx-auto" />
                      <div className="space-y-2">
                        <p className="font-bold text-xl">شكراً {fullName.split(' ')[0]}! 😊</p>
                        <p className="font-bold text-xl">Thanks {fullName.split(' ')[0]}! 😊</p>
                        <p className="text-sm text-muted-foreground leading-relaxed mt-2">
                          Your order has been placed successfully.<br/>
                          We'll be reaching out to confirm your delivery details via <span className="text-foreground font-medium">email</span> and <span className="text-foreground font-medium">WhatsApp / mobile</span>.
                        </p>
                      </div>
                      <button onClick={handleClose} className="px-6 py-2 rounded-lg bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90 transition-colors">{t('cart.continueShopping')}</button>
                    </div>
                  ) : (
                    <>
                      <button onClick={handleCheckout} disabled={isPlacing}
                        className="w-full py-3 rounded-lg bg-primary text-primary-foreground font-semibold text-sm hover:bg-primary/90 transition-colors disabled:opacity-60 flex items-center justify-center gap-2">
                        {isPlacing ? <><Loader2 className="h-4 w-4 animate-spin" /> Placing Order…</> : <>Place Order — {t('products.egp')} {total.toLocaleString()}</>}
                      </button>
                      {submitted && Object.keys(errors).length > 0 && (
                        <p className="text-xs text-destructive text-center -mt-2">Please fill in all required fields before placing your order.</p>
                      )}
                    </>
                  )}

                </div>
              )}
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default CartDrawer;







